// src/components/ui/ContextSwitcher.jsx
"use client";
import React, { useState } from "react";
import { ChevronDown, Building2, User, Plus, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./dropdown-menu";
import { Badge } from "./badge";
import { useOrganizations } from "../../hooks/useOrganizations";
import { useNavigate } from "react-router-dom";

/**
 * Context Switcher Component
 * Permite cambiar entre Personal Account y Organizations
 * Similar a GitHub/Linear/Notion
 * 
 * Ahora integrado con OrganizationContext
 */
export default function ContextSwitcher() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  
  const {
    currentContext,
    personalAccount,
    organizations = [],
    changeContext,
    organizationsLoading
  } = useOrganizations();

  // Si no hay personal account aún, mostrar loading
  if (!personalAccount || switching) {
    return (
      <button className="flex items-center gap-2 px-3 py-2 rounded-lg
                        bg-black/40 border border-white/20 text-white">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
        <span className="font-medium">
          {switching ? 'Switching...' : 'Loading...'}
        </span>
      </button>
    );
  }

  const getCurrentContextDisplay = () => {
    if (currentContext?.type === 'PERSONAL') {
      return {
        icon: <User className="w-4 h-4" />,
        name: 'Personal',
        role: null
      };
    }
    
    if (currentContext?.type === 'ORGANIZATION') {
      const org = organizations?.find(o => o.id === currentContext.id);
      return {
        icon: <Building2 className="w-4 h-4" />,
        name: org?.name || 'Organization',
        role: org?.role || 'member'
      };
    }

    return {
      icon: <User className="w-4 h-4" />,
      name: 'Personal',
      role: null
    };
  };

  const handleContextChange = async (newContext) => {
    setSwitching(true);
    try {
      await changeContext(newContext);
    } catch (error) {
      console.error('❌ Failed to switch context:', error);
    } finally {
      setSwitching(false);
      setOpen(false);
    }
  };

  const handleCreateOrganization = () => {
    setOpen(false);
    navigate('/organizations');
  };

  const display = getCurrentContextDisplay();

  const getRoleBadge = (role) => {
    const roleColors = {
      owner: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      admin: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      member: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      viewer: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
    };

    return (
      <Badge className={`text-xs ${roleColors[role] || roleColors.member}`}>
        {role?.toUpperCase()}
      </Badge>
    );
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 px-3 py-2 rounded-lg
                          bg-black/40 border border-white/20 hover:border-[#7c3aed]
                          text-white transition-colors">
          {display.icon}
          <span className="font-medium">{display.name}</span>
          {display.role && getRoleBadge(display.role)}
          <ChevronDown className="w-4 h-4 ml-1 opacity-50" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent 
        align="start" 
        className="w-80 bg-[#0a0a0a] border-white/20 backdrop-blur-xl"
      >
        {/* Personal Account */}
        <DropdownMenuLabel className="text-gray-400 text-xs uppercase">
          Personal
        </DropdownMenuLabel>
        <DropdownMenuItem
          onClick={() => handleContextChange({ type: 'PERSONAL', id: personalAccount?.id })}
          className="flex items-center justify-between py-3 cursor-pointer
                     hover:bg-white/5 focus:bg-white/5"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-white font-medium">
                {personalAccount?.name || 'Personal Account'}
              </div>
              <div className="text-xs text-gray-400">
                {personalAccount?.description || 'Your private workspace'}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {personalAccount?.is_active && (
              <div className="w-2 h-2 rounded-full bg-green-400"></div>
            )}
            {currentContext?.type === 'PERSONAL' && (
              <Check className="w-4 h-4 text-[#7c3aed]" />
            )}
          </div>
        </DropdownMenuItem>

        {/* Organizations */}
        {organizations && organizations.length > 0 && (
          <>
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuLabel className="text-gray-400 text-xs uppercase">
              Organizations
            </DropdownMenuLabel>
            {organizations.map((org) => (
              <DropdownMenuItem
                key={org.id}
                onClick={() => handleContextChange({ type: 'ORGANIZATION', id: org.id })}
                className="flex items-center justify-between py-3 cursor-pointer
                           hover:bg-white/5 focus:bg-white/5"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-xl">
                    {org.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="text-white font-medium">{org.name}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-gray-400">{org.domain || 'No domain'}</span>
                      {getRoleBadge(org.role)}
                    </div>
                  </div>
                </div>
                {currentContext?.type === 'ORGANIZATION' && currentContext?.id === org.id && (
                  <Check className="w-4 h-4 text-[#7c3aed]" />
                )}
              </DropdownMenuItem>
            ))}
          </>
        )}

        {/* Manage Organizations */}
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuItem
          onClick={handleCreateOrganization}
          className="flex items-center gap-3 py-3 cursor-pointer
                     hover:bg-white/5 focus:bg-white/5 text-[#7c3aed]"
        >
          <div className="w-8 h-8 rounded-lg border-2 border-[#7c3aed] 
                        flex items-center justify-center">
            <Building2 className="w-4 h-4" />
          </div>
          <span className="font-medium">Manage All</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
